import { FC } from 'react'
import {
  Avatar,
  Box,
  Button,
  capitalize,
  Chip,
  makeStyles,
  MuiStack,
  Typography,
} from '@perk-ui/core'
import clsx from 'clsx'
import { PlusCircle } from 'react-feather'
import { useQueryClient } from 'react-query'
import { useHistory, useParams } from 'react-router'

import TreatmentDetail from '../../../components/TreatmentDetail'
import QueryKeys from '../../../features/query-hooks/QueryKeys'
import useBodySites from '../../../features/query-hooks/useBodySites'
import usePatient from '../../../features/query-hooks/usePatient'
import usePatientCases from '../../../features/query-hooks/usePatientCases'
import PatientSelfOnboardUrls from '../PatientSelfOnboardUrls'

interface LocationParams {
  patientId: string | undefined
}

interface PatientDetailsProps {}

const useStyles = makeStyles((theme) => ({
  nameAvatar: {
    backgroundColor: '#E3F0F7',
    color: '#56AAE8',
    width: 126,
    height: 126,
    fontSize: '40px',
    fontWeight: '700',
    marginRight: theme.spacing(4),
  },
  addBodySiteButton: {
    width: '100%',
    borderRadius: '17px',
    border: '2px solid transparent',
    padding: theme.spacing(1, 3, 1, 1),
    marginBottom: theme.spacing(2),
    boxShadow: 'none',
    backgroundColor: '#F8F8F8',
    '& .button-text-span': {
      width: '100%',
      textAlign: 'left',
      color: '#000',
    },
    '&:hover': {
      border: '2px solid #56AAE8',
      '& .button-text-span': {
        color: '#56AAE8',
      },
    },
  },
  bodyAvatar: {
    backgroundColor: '#E3F2FD',
    width: '62px',
    height: '62px',
    margin: theme.spacing(0, 2),
  },
  bodyAvatarIcon: {
    width: '32px',
    height: '32px',
    color: '#42A5F5',
  },
  addText: {
    ontFamily: 'DM Sans',
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '40px',
    marginLeft: '16px',
  },
  patientName: {
    fontFamily: 'DM Sans',
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: '24px',
    marginBottom: '12px',
  },
  patientText: {
    fontFamily: 'DM Sans',
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '24px',
    color: '#A3A3A3',
  },
  diagnosesBox: {
    backgroundColor: '#F8F8F8',
    borderRadius: '8px',
    padding: '16px 16px 8px 16px',
  },
  diagnosesTitle: {
    fontFamily: 'DM Sans',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '24px',
    marginBottom: '8px',
    textAlign: 'left',
    color: '#000',
    padding: 0,
    justifyContent: 'left',
    minHeight: 'auto',
    '&:hover': {
      opacity: 0.7,
    },
  },
  internalDiagnosesBox: {
    borderRadius: '8px',
    paddingBottom: '8px',
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '8px',
    border: '1px solid #00000014',
    marginBottom: '8px',
  },
  medicalCondition: {
    fontFamily: 'DM Sans',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '24px',
  },
  severityText: {
    fontFamily: 'DM Sans',
    fontSize: '14px',
    lineHeight: '20px',
    color: '#767676',
  },
  locations: {
    fontFamily: 'DM Sans',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 400,
  },
  severityChip: {
    fontWeight: 'bold',
    fontSize: '0.75rem',
    backgroundColor: 'transparent',
    marginLeft: '8px',
  },
  severityMild: {
    borderColor: '#27ADE1',
    '& .MuiChip-label': {
      color: '#27ADE1',
    },
  },
  severityModerate: {
    borderColor: '#F79A48',
    '& .MuiChip-label': {
      color: '#F79A48',
    },
  },
  severitySevere: {
    borderColor: '#C00202',
    '& .MuiChip-label': {
      color: '#C00202',
    },
  },
  diagnosisTypeChip: {
    alignSelf: 'start',
    fontWeight: 'bold',
    fontSize: '0.75rem',
  },
  primaryChip: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
  },
  secondaryChip: {
    backgroundColor: '#e0e0e0',
    color: '#616161',
  },
  stackElement: {
    marginBottom: '8px',
  },
  treatmentBox: {
    border: '1px solid #00000014',
    borderRadius: '8px',
    paddingLeft: '8px',
    marginTop: '8px',
    marginBottom: '8px',
  },
  bodyTitle: {
    fontFamily: 'DM Sans',
    marginBottom: '8px',
    marginTop: '10px',
    display: 'flex',
    '& span.partSide': {
      backgroundColor: '#F1F1F1',
      padding: '8px 12px',
      marginLeft: '8px',
      borderRadius: '16px',
      fontWeight: 400,
      fontSize: '14px',
    },
  },
  addSessionButton: {
    minHeight: '40px',
    padding: theme.spacing(1.5, 3),
    '& .MuiButton-endIcon': {
      marginLeft: theme.spacing(3),
    },
  },
  caseContainer: {
    paddingBottom: theme.spacing(2),
  },
  bodyTitleButton: {
    padding: 0,
    minHeight: 'auto',
    color: theme.palette.common.black,
    justifyContent: 'left',
    textTransform: 'none',
    '&:hover': {
      opacity: 0.7,
    },
  },
}))

const PatientDetails: FC<PatientDetailsProps> = () => {
  const classes = useStyles()
  const { patientId } = useParams<LocationParams>()
  const history = useHistory()
  const queryClient = useQueryClient()

  const { data: patient } = usePatient(patientId)

  const { data: patientCases = [] } = usePatientCases(patientId)

  const { data: bodySites = [] } = useBodySites()
  const findBodySiteById = (bodySiteId: string) => {
    return bodySites.find((site) => site.id === bodySiteId)?.name
  }

  return (
    <Box
      sx={{
        maxWidth: '787px',
        width: '100%',
        height: '100%',
        marginBottom: '40px',
      }}
    >
      <MuiStack
        direction="row"
        alignItems="center"
        sx={{ marginBottom: '40px' }}
      >
        <Avatar className={classes.nameAvatar}>
          {patient?.firstName.charAt(0)}
          {patient?.lastName.charAt(0)}
        </Avatar>
        <MuiStack>
          <Typography className={classes.patientName}>
            {patient?.firstName} {patient?.lastName}
          </Typography>
          <Typography className={classes.patientText}>Patient</Typography>
        </MuiStack>
      </MuiStack>
      <Button
        className={classes.addBodySiteButton}
        variant="outlined"
        startIcon={
          <Avatar className={classes.bodyAvatar}>
            <img
              src={`${process.env.PUBLIC_URL}/assets/bodyIcons/ankle-left.svg`}
              className={classes.bodyAvatarIcon}
            />
          </Avatar>
        }
        endIcon={<PlusCircle size={28} />}
        onClick={() =>
          history.push(PatientSelfOnboardUrls.addCase.create(patient?.id || ''))
        }
      >
        <span className="button-text-span">Add new body part</span>
      </Button>
      {patientCases.map((patientCase) => {
        const bodyPartName = findBodySiteById(patientCase.bodySiteId)
        const bodyPartSide = bodyPartName?.includes('Left') ? (
          <span className="partSide">L</span>
        ) : bodyPartName?.includes('Right') ? (
          <span className="partSide">R</span>
        ) : (
          ''
        )
        return (
          <Box key={patientCase.id} className={classes.caseContainer}>
            <Button
              variant="text"
              className={classes.bodyTitleButton}
              onClick={() => {
                queryClient.invalidateQueries(
                  QueryKeys.surveyResponseGroupsAlt({
                    currentSiteId: patientCase.siteId || '',
                    patientId: patient?.id || '',
                    patientCaseId: patientCase.id || '',
                  }),
                )
                history.push(
                  PatientSelfOnboardUrls.survey.create(
                    patient?.id || '',
                    patientCase.id || '',
                  ),
                )
              }}
            >
              <Typography className={classes.bodyTitle}>
                <span
                  style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    alignSelf: 'center',
                  }}
                >
                  {bodyPartName
                    ?.replaceAll('(Left)', '')
                    .replaceAll('(Right)', '')}
                </span>
                {bodyPartSide}
              </Typography>
            </Button>
            <MuiStack
              className={classes.diagnosesBox}
              sx={{ marginBottom: '12px' }}
            >
              <Button
                variant="text"
                className={classes.diagnosesTitle}
                onClick={() =>
                  history.push(
                    PatientSelfOnboardUrls.addDiagnoses.create(
                      patient?.id || '',
                      patientCase.id || '',
                    ),
                  )
                }
              >
                Diagnoses
              </Button>
              {patientCase.patientDiagnoses?.map((diagnosis) => (
                <div
                  key={diagnosis.id}
                  className={classes.internalDiagnosesBox}
                >
                  <MuiStack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    className={classes.stackElement}
                  >
                    <Typography className={classes.medicalCondition}>
                      {diagnosis.medicalCondition?.name}
                    </Typography>
                    <Chip
                      label={diagnosis.isPrimary ? 'Primary' : 'Secondary'}
                      size="small"
                      className={clsx(
                        classes.diagnosisTypeChip,
                        diagnosis.isPrimary && classes.primaryChip,
                        !diagnosis.isPrimary && classes.secondaryChip,
                      )}
                    />
                  </MuiStack>
                  <MuiStack
                    direction="row"
                    justifyContent="start"
                    alignItems="center"
                    className={classes.stackElement}
                  >
                    <Typography className={classes.severityText}>
                      Severity:
                    </Typography>
                    <Chip
                      label={capitalize(diagnosis.severity)}
                      variant="outlined"
                      size="small"
                      className={clsx(
                        classes.severityChip,
                        diagnosis.severity === 'mild' && classes.severityMild,
                        diagnosis.severity === 'moderate' &&
                          classes.severityModerate,
                        diagnosis.severity === 'severe' &&
                          classes.severitySevere,
                      )}
                    />
                  </MuiStack>
                  {diagnosis.locations.length > 0 && (
                    <MuiStack direction="row">
                      <Typography className={classes.severityText}>
                        Location Detail:{' '}
                      </Typography>
                      <Typography>
                        &nbsp;
                        {diagnosis.locations.map((l) => l.name).join(', ')}
                      </Typography>
                    </MuiStack>
                  )}
                </div>
              ))}
            </MuiStack>

            {patientCase.patientTreatments?.map((treatment) => {
              if (treatment.parentId) return
              return (
                <MuiStack
                  className={classes.diagnosesBox}
                  key={treatment.id}
                  sx={{ marginBottom: '12px' }}
                >
                  <MuiStack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Button
                      variant="text"
                      className={classes.diagnosesTitle}
                      onClick={() =>
                        history.push(
                          PatientSelfOnboardUrls.addTreatment.create(
                            patient?.id || '',
                            patientCase.id || '',
                          ),
                        )
                      }
                    >
                      Treatment
                    </Button>
                    <Button
                      endIcon={<PlusCircle />}
                      className={classes.addSessionButton}
                      onClick={() =>
                        history.push(
                          PatientSelfOnboardUrls.addSession.create(
                            patientId || '',
                            patientCase.id || '',
                            treatment.id,
                          ),
                        )
                      }
                    >
                      Add new session
                    </Button>
                  </MuiStack>
                  <Box className={classes.treatmentBox}>
                    <TreatmentDetail
                      treatment={treatment}
                      withDetails
                      patientId={patientId}
                      showPillDate
                      boxPb={0}
                      boxPt={1}
                      isCollapse={false}
                      sessions={patientCase.patientTreatments?.filter(
                        (pt) => pt.parentId == treatment.id,
                      )}
                    />
                  </Box>
                </MuiStack>
              )
            })}
          </Box>
        )
      })}
    </Box>
  )
}

export default PatientDetails
